/**
 * Playback, prefetch, and the Cache API store for clips.
 *
 * The client prefetches all audio on session open, so a dictation block never
 * waits on the network (docs/04). Clips are immutable — the hash is the
 * content — so they are cached first and never revalidated.
 */

const CACHE_NAME = "fala-audio-v1";

let current: HTMLAudioElement | undefined;

export async function prefetch(urls: string[]): Promise<void> {
	const wanted = urls.filter((url) => url !== "");
	if (wanted.length === 0 || !("caches" in window)) return;
	try {
		const cache = await caches.open(CACHE_NAME);
		await Promise.all(
			wanted.map(async (url) => {
				if ((await cache.match(url)) !== undefined) return;
				const response = await fetch(url);
				if (response.ok) await cache.put(url, response.clone());
			}),
		);
	} catch {
		// A prefetch failure just means the clip loads on demand.
	}
}

export interface PlayHandle {
	stop: () => void;
}

export function play(
	url: string,
	options: { rate?: number; onProgress?: (fraction: number) => void; onEnd?: () => void } = {},
): PlayHandle {
	stop();
	const audio = new Audio(url);
	audio.playbackRate = options.rate ?? 1;
	audio.preload = "auto";
	current = audio;

	if (options.onProgress !== undefined) {
		audio.addEventListener("timeupdate", () => {
			const duration = audio.duration;
			if (Number.isFinite(duration) && duration > 0) options.onProgress?.(audio.currentTime / duration);
		});
	}
	audio.addEventListener("ended", () => {
		options.onProgress?.(1);
		options.onEnd?.();
		if (current === audio) current = undefined;
	});

	void audio.play().catch(() => {
		// Autoplay policies: the learner taps play, so this only fires on an
		// unavailable clip. The UI stays usable either way.
		options.onEnd?.();
	});

	return {
		stop: (): void => {
			audio.pause();
			if (current === audio) current = undefined;
		},
	};
}

export function stop(): void {
	current?.pause();
	current = undefined;
}
